import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { Users, Phone, TrendingUp, AlertCircle, Bell, Clock, CheckCircle, X, IndianRupee, UserCheck, Trophy, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../lib/utils';

const SalesDashboard = () => {
  const [stats, setStats] = useState(null);
  const [dueReminders, setDueReminders] = useState([]);
  const [showReminderBucket, setShowReminderBucket] = useState(false);
  const [loading, setLoading] = useState(true);
  const user = authService.getCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role === 'caller') {
      fetchCallerStats();
      fetchDueReminders();
    } else {
      fetchStats();
    }
  }, [user]);

  const fetchCallerStats = async () => {
    try {
      // For callers, fetch their own leads stats
      const response = await api.getLeads({ limit: 10000 });
      const leads = response.data.leads || [];
      
      // Get today's date
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const total_leads = leads.length;
      const hot_leads = leads.filter(l => l.status === 'hot').length;
      const warm_leads = leads.filter(l => l.status === 'warm').length;
      const customers = leads.filter(l => l.status === 'customer').length;
      
      // Today's customers
      const today_customers = leads.filter(l => {
        if (l.status !== 'customer' || !l.sold_at) return false;
        const soldDate = new Date(l.sold_at);
        return soldDate >= today;
      }).length;
      
      // Get due reminders count
      const dueResponse = await api.getDueReminders();
      const due_reminders = dueResponse.data?.length || 0;
      
      setStats({
        total_leads,
        hot_leads,
        warm_leads,
        customers,
        today_customers,
        due_reminders
      });
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const [statsResponse, onlineCallersResponse] = await Promise.all([
        api.getStats(),
        api.getOnlineCallers()
      ]);
      
      const data = statsResponse.data;
      const onlineCallers = onlineCallersResponse.data || [];
      
      // Transform API response - now using today's stats
      setStats({
        // Today's performance
        today_leads: data.today?.leads || 0,
        today_hot: data.today?.hot || 0,
        today_warm: data.today?.warm || 0,
        today_customers: data.today?.customers || 0,
        today_revenue: data.today?.revenue || 0,
        today_conversion_rate: data.today?.conversion_rate || 0,
        // Totals
        total_leads: data.total?.leads || 0,
        total_customers: data.total?.customers || 0,
        total_conversion_rate: data.total?.conversion_rate || 0,
        total_callers: data.callers || 0,
        available_callers: onlineCallers.length,
        unassigned_leads: data.unassigned || 0,
        // Top products (today)
        top_products: data.top_products || []
      });
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchDueReminders = async () => {
    try {
      const response = await api.getDueReminders();
      setDueReminders(response.data);
    } catch (error) {
      console.error('Failed to fetch due reminders:', error);
    }
  };

  const handleMarkComplete = async (reminderId) => {
    try {
      await api.markReminderComplete(reminderId);
      toast.success('Reminder marked as complete');
      fetchDueReminders();
      fetchStats();
    } catch (error) {
      toast.error('Failed to mark reminder complete');
    }
  };

  const handleOpenLead = (leadId) => {
    navigate('/sales/leads', { state: { openLeadId: leadId } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600 font-body">Loading dashboard...</div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, bgColor, onClick, subtitle }) => (
    <div 
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`} 
      onClick={onClick}
      className={`bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all p-6 ${onClick ? 'cursor-pointer hover:scale-105 active:scale-95' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 font-body mb-2">{title}</p>
          <p className="text-3xl font-bold font-headings" style={{ color }}>{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${bgColor}`}>
          <Icon size={24} style={{ color }} />
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="sales-dashboard" className="max-w-[1600px] mx-auto space-y-8">
      <div>
        <h1 className="text-4xl font-bold font-headings uppercase tracking-tight text-slate-900 mb-2">
          Sales Dashboard
        </h1>
        <p className="text-slate-600 font-body">Today's Performance Overview</p>
      </div>

      {/* Stats Grid */}
      {user?.role === 'caller' ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            <StatCard 
              title="My Leads" 
              value={stats?.total_leads || 0} 
              icon={Phone} 
              color="#0F172A" 
              bgColor="bg-slate-100"
              onClick={() => navigate('/sales/leads')}
            />
            <StatCard 
              title="Hot Leads" 
              value={stats?.hot_leads || 0} 
              icon={TrendingUp} 
              color="#EF4444" 
              bgColor="bg-red-50"
              onClick={() => navigate('/sales/leads', { state: { statusFilter: 'hot' } })}
            />
            <StatCard 
              title="Warm Leads" 
              value={stats?.warm_leads || 0} 
              icon={TrendingUp} 
              color="#F59E0B" 
              bgColor="bg-amber-50"
              onClick={() => navigate('/sales/leads', { state: { statusFilter: 'warm' } })}
            />
            <StatCard 
              title="Today's Sales" 
              value={stats?.today_customers || 0} 
              icon={UserCheck} 
              color="#22C55E" 
              bgColor="bg-green-50"
              onClick={() => navigate('/sales/leads', { state: { statusFilter: 'customer' } })}
              subtitle={`Total: ${stats?.customers || 0}`}
            />
            <StatCard 
              title="Due Reminders" 
              value={stats?.due_reminders || 0} 
              icon={Bell} 
              color="#DC2626" 
              bgColor="bg-red-50"
              onClick={() => setShowReminderBucket(!showReminderBucket)}
            />
          </div>

          {/* Due Reminders Bucket */}
          {showReminderBucket && (
            <div className="bg-white rounded-xl border-2 border-red-200 shadow-sm overflow-hidden">
              <div className="p-4 bg-red-50 border-b border-red-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell size={24} className="text-red-600" />
                  <h2 className="text-xl font-semibold font-headings text-red-700">
                    Due Reminders ({dueReminders.length})
                  </h2>
                </div>
                <button
                  onClick={() => setShowReminderBucket(false)}
                  className="p-1 hover:bg-red-100 rounded-md transition-colors"
                >
                  <X size={20} className="text-red-600" />
                </button>
              </div>

              {dueReminders.length === 0 ? (
                <div className="p-8 text-center">
                  <CheckCircle size={48} className="mx-auto text-green-400 mb-3" />
                  <p className="text-slate-600 font-body">No due reminders! You're all caught up.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                  {dueReminders.map((reminder) => (
                    <div
                      key={reminder.id}
                      data-testid={`due-reminder-${reminder.id}`}
                      className="p-4 hover:bg-red-50/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900 font-body truncate">
                              {reminder.lead_name}
                            </h3>
                            {reminder.lead_status && (
                              <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded-full ${
                                reminder.lead_status === 'hot' ? 'bg-red-100 text-red-700' :
                                reminder.lead_status === 'warm' ? 'bg-amber-100 text-amber-700' :
                                reminder.lead_status === 'cold' ? 'bg-slate-100 text-slate-600' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {reminder.lead_status}
                              </span>
                            )}
                          </div>
                          
                          <p className="text-sm text-slate-600 font-mono mb-1">
                            {reminder.lead_phone}
                          </p>
                          
                          <div className="flex items-center gap-2 text-xs text-red-600 font-mono">
                            <Clock size={14} />
                            <span>Due: {formatDate(reminder.reminder_time)}</span>
                          </div>

                          {reminder.note && (
                            <p className="text-sm text-slate-600 font-body mt-2 bg-slate-50 p-2 rounded">
                              {reminder.note}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleOpenLead(reminder.lead_id)}
                            data-testid={`open-lead-${reminder.id}`}
                            className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-body"
                          >
                            <Phone size={14} />
                            <span>Call</span>
                          </button>
                          <button
                            onClick={() => handleMarkComplete(reminder.id)}
                            data-testid={`complete-reminder-${reminder.id}`}
                            className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-body"
                          >
                            <CheckCircle size={14} />
                            <span>Done</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Today's Performance Header */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-700 rounded-xl p-6 text-white">
            <h2 className="text-lg font-semibold mb-4">Today's Performance</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              <div>
                <p className="text-slate-300 text-xs uppercase">New Leads</p>
                <p className="text-3xl font-bold">{stats?.today_leads || 0}</p>
              </div>
              <div>
                <p className="text-slate-300 text-xs uppercase">Hot Leads</p>
                <p className="text-3xl font-bold text-red-400">{stats?.today_hot || 0}</p>
              </div>
              <div>
                <p className="text-slate-300 text-xs uppercase">Sales</p>
                <p className="text-3xl font-bold text-green-400">{stats?.today_customers || 0}</p>
              </div>
              <div>
                <p className="text-slate-300 text-xs uppercase">Revenue</p>
                <p className="text-3xl font-bold text-emerald-400">₹{(stats?.today_revenue || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-slate-300 text-xs uppercase">Conversion</p>
                <p className="text-3xl font-bold text-blue-400">{stats?.today_conversion_rate || 0}%</p>
              </div>
            </div>
          </div>

          {/* Total Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            <StatCard 
              title="Total Leads" 
              value={stats?.total_leads || 0} 
              icon={Phone} 
              color="#0F172A" 
              bgColor="bg-slate-100"
              onClick={() => navigate('/sales/leads')}
            />
            <StatCard 
              title="Total Customers" 
              value={stats?.total_customers || 0} 
              icon={UserCheck} 
              color="#22C55E" 
              bgColor="bg-green-50"
              onClick={() => navigate('/sales/leads', { state: { statusFilter: 'customer' } })}
            />
            <StatCard 
              title="Available Callers" 
              value={stats?.available_callers || 0} 
              icon={Users} 
              color="#10B981" 
              bgColor="bg-emerald-50"
              onClick={() => navigate('/sales/users', { state: { roleFilter: 'caller' } })}
              subtitle={`Total: ${stats?.total_callers || 0}`}
            />
            <StatCard 
              title="Unassigned" 
              value={stats?.unassigned_leads || 0} 
              icon={AlertCircle} 
              color="#F59E0B" 
              bgColor="bg-amber-50"
              onClick={() => navigate('/sales/leads', { state: { unassigned: true } })}
            />
            <StatCard 
              title="Total Conversion" 
              value={`${stats?.total_conversion_rate || 0}%`} 
              icon={TrendingUp} 
              color="#8B5CF6" 
              bgColor="bg-purple-50"
            />
          </div>

          {/* Top Selling Products Today */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Trophy size={20} className="text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold font-headings text-slate-900">Top Selling Products</h3>
                <p className="text-xs text-slate-500">Today's sales</p>
              </div>
            </div>
            
            {stats?.top_products?.length > 0 ? (
              <div className="space-y-3">
                {stats.top_products.map((product, index) => (
                  <div 
                    key={product.name} 
                    className={`flex items-center justify-between p-4 rounded-xl ${
                      index === 0 ? 'bg-amber-50 border-2 border-amber-200' :
                      index === 1 ? 'bg-slate-100 border border-slate-200' :
                      'bg-slate-50 border border-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? 'bg-amber-500 text-white' :
                        index === 1 ? 'bg-slate-400 text-white' :
                        'bg-amber-700 text-white'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{product.name}</p>
                        <p className="text-sm text-slate-500">{product.count} sold today</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600">₹{(product.revenue || 0).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Package size={48} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 font-body">No product sales today</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="text-2xl font-semibold font-headings text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="/sales/leads"
            data-testid="quick-action-view-leads"
            className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Phone size={24} className="text-slate-700" />
            <div>
              <p className="font-medium font-body text-slate-900">View All Leads</p>
              <p className="text-xs text-slate-500 font-body">Manage your lead pipeline</p>
            </div>
          </a>
          {['admin', 'manager'].includes(user?.role) && (
            <a
              href="/sales/users"
              data-testid="quick-action-manage-users"
              className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <Users size={24} className="text-slate-700" />
              <div>
                <p className="font-medium font-body text-slate-900">Manage Users</p>
                <p className="text-xs text-slate-500 font-body">Add or edit team members</p>
              </div>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;
